<i18n>
{
	"en": {
		"noFreightLines": "There are no freight lines yet.",
		"freightLines": "Freight Lines",
		"packages": " No packages | {count} packages | {count} packages",
		"numberOfPackages": "",
		"description": "Description",
		"grossWeight": "Gr. Weight",
		"chargableWeight": "Chargable Weight",
		"marks": "Marks",
		"netWeight": "Nt. Weight",
		"quantity": "Quantity",
		"packageTypeCode": "Code",
		"pallets": "Pallets",
		"height": "Height",
		"width": "Width",
		"length": "Length",
		"volume": "Volume",
		"loadingMeters": "Loading meters",
		"palletType": "Pallet Type",
		"palletPriceFactorPrice": "P.P. Price",
		"palletPriceFactorTruck": "P.P. Car"
	},
	"no": {
		"noFreightLines": "Det er ingen fraktlinjer ennå.",
		"freightLines": "Fraktlinjer",
		"packages": "Ingen kolli | {count} kolli | {count} kolli",
		"numberOfPackages": "",
		"description": "Beskrivelse",
		"grossWeight": "Br. vekt",
		"chargableWeight": "Fraktberegningsvekt",
		"marks": "Merknad",
		"netWeight": "Nt. vekt",
		"quantity": "Antall",
		"packageTypeCode": "Kode",
		"pallets": "Paller",
		"height": "Høyde",
		"width": "Bredde",
		"length": "Lengde",
		"volume": "Volum",
		"loadingMeters": "Lastemeter",
		"palletType": "Palletype",
		"palletPriceFactorPrice": "P.P. Pris",
		"palletPriceFactorTruck": "P.P. Bil"
	}
}
</i18n>
<template>
	<div class="freightlines">
		<div class="freightlines-header">
			<div>
				<t-panel-header
					:text="$t('freightLines')"
					icon="orders"
					size="md"
				/>
				<div class="freightlines-header-details">
					{{$tc('packages', numberOfPackages)}}
				</div>
			</div>
			<div>
				<t-icon-button
					v-if="freightLines.length > 1"
					:disabled="!rowsSelected"
					icon="minus"
					type="bp"
					appearance="minimal"
					intent="danger"
					style="margin-right: 4px"
					@click="removeLine"
				/>
				<t-icon-button
					icon="plus"
					type="bp"
					appearance="minimal"
					@click="addLine"
				/>
			</div>
		</div>

		<t-grid
			v-if="freightLines && freightLines.length > 0"
			ref="grid"
			:columnDefinitions="columnDefinitions"
			:rowHeight="42"
			:items="freightLines"
			:showFilters="false"
			:sort="[{field: 'lineNo', direction: 'asc'}]"
			:selectableMode="['checkbox']"
			selectable
			disableSelectCheckboxTabbing
			disableContainerFocus
			idKey="lineNo"
			class="freightlines-grid"
			@selected="onRowsSelected"
		>
			<template #lineNo="{ item }">
				<t-pill size="small">{{item.lineNo}}</t-pill>
			</template>
			<template #packageTypeCode="{ item, columnIndex, rowIndex }">
				<PackageTypesFormInput
					:value="item.packageTypeCode"
					noTitle
					@select="onSelectPackageType(item.lineNo, $event)"
				/>
			</template>
			<template #marks="{ item, columnIndex, rowIndex }">
				<t-text-input
					:value="item.marks"
					@change="onStrInput(item.lineNo, 'marks', $event )"
				/>
			</template>
			<template #description="{ item, columnIndex, rowIndex }">
				<t-text-input
					:value="item.description"
					@change="onStrInput(item.lineNo, 'description', $event )"
				/>
			</template>
			<template #netWeight="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.netWeight,1)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'netWeight', truncateDecimal($event,1) )"
				/>
			</template>
			<template #grossWeight="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.grossWeight,1)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'grossWeight', truncateDecimal($event,1) )"
				/>
			</template>
			<template #palletPackageCode="{ item, columnIndex, rowIndex }">
				<PalletPackageTypesFormInput
					:value="item.palletPackageCode"
					noTitle
					@select="onSelectPalletPackageType(item.lineNo, $event)"
				/>
			</template>
			<template #palletPriceFactorPrice="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.palletPriceFactorPrice"
					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'palletPriceFactorPrice', $event )"
				/>
			</template>
			<template #palletPriceFactorTruck="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.palletPriceFactorTruck"
					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'palletPriceFactorTruck', $event )"
				/>
			</template>
			<template #numberOfPallets="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.numberOfPallets"
					min="0"
					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'numberOfPallets', $event )"
				/>
			</template>
			<template #numberOfUnits="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="item.numberOfUnits"
					min="0"
					@focus="$event.target.select()"
					@change="onIntInput( item.lineNo, 'numberOfUnits', $event )"
				/>
			</template>
			<template #length="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.length, 1)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'length', truncateDecimal($event,1) )"
				/>
			</template>
			<template #width="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.width, 1)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'width', truncateDecimal($event,1) )"
				/>
			</template>
			<template #height="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.height,1)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'height', truncateDecimal($event,1) )"
				/>
			</template>
			<template #volume="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="formatVolume(item.volume)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'volume', formatVolume($event) )"
				/>
			</template>
			<template #loadingMeters="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.loadingMeters,2)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'loadingMeters', truncateDecimal($event,2) )"
				/>
			</template>
			<template #chargableWeight="{ item, columnIndex, rowIndex }">
				<t-number-input
					:value="truncateDecimal(item.chargableWeight, 1)"
					min="0"
					@focus="$event.target.select()"
					@change="onFloatInput( item.lineNo, 'chargableWeight', truncateDecimal($event,1) )"
				/>
			</template>
		</t-grid>

		<t-no-content-panel v-else :text="$t('noFreightLines')" icon="order" />
	</div>
</template>

<script>

// TODO, this code was used previously for freightlines, current version saving on state which does not work for
// template-usage.
// Same for priceLines and freeText.

import { truncateDecimal } from '@/core';
import { mapMutations } from '../store/orderTemplates.store';
import PackageTypesFormInput from '@/modules/packageTypes/PackageTypesFormInput';
import PalletPackageTypesFormInput from '@/modules/packageTypes/PalletPackageTypesFormInput';

export default {
	name: 'FreightLines',
	components: { PackageTypesFormInput, PalletPackageTypesFormInput },
	props: { order: Object, events: Object },
	data() {
		return {
			rowsSelected: 0
		};
	},
	computed: {
		freightLines() {
			return this.order.freightLines;
		},
		numberOfPackages() {
			return this.order.numberOfPackages;
		},
		columnDefinitions() {
			return [
				{
					field: 'lineNo',
					displayName: '#',
					width: 35,
					center: true,
				},
				{
					field: 'packageTypeCode',
					displayName: this.$t('packageTypeCode'),
					width: 100,
					resizable: true,
				},
				{
					field: 'marks',
					displayName: this.$t('marks'),
					minWidth: 150,
					resizable: true,
				},
				{
					field: 'description',
					displayName: this.$t('description'),
					minWidth: 150,
					resizable: true,
				},
				{
					field: 'netWeight',
					displayName: this.$t('netWeight'),
					type: 'number',
					width: 70
				},
				{
					field: 'grossWeight',
					displayName: this.$t('grossWeight'),
					type: 'number',
					width: 70
				},
				{
					field: 'numberOfUnits',
					displayName: this.$t('quantity'),
					type: 'number',
					width: 60,
				},
				{
					field: 'palletPackageCode',
					displayName: this.$t('palletType'),
					width: 100,
					resizable: true,
				},
				{
					field: 'palletPriceFactorPrice',
					displayName: this.$t('palletPriceFactorPrice'),
					type: 'number',
					width: 60,
					resizable: true,
				},
				{
					field: 'palletPriceFactorTruck',
					displayName: this.$t('palletPriceFactorTruck'),
					type: 'number',
					width: 60,
					resizable: true,
				},
				{
					field: 'numberOfPallets',
					displayName: this.$t('pallets'),
					type: 'number',
					width: 60
				},
				{
					field: 'length',
					displayName: this.$t('length'),
					type: 'number',
					width: 60
				},
				{
					field: 'width',
					displayName: this.$t('width'),
					type: 'number',
					width: 60
				},
				{
					field: 'height',
					displayName: this.$t('height'),
					type: 'number',
					width: 60
				},
				{
					field: 'volume',
					displayName: this.$t('volume'),
					type: 'number',
					width: 80
				},
				{
					field: 'loadingMeters',
					displayName: this.$t('loadingMeters'),
					type: 'number',
					width: 80
				},
				{
					field: 'chargableWeight',
					displayName: this.$t('chargableWeight'),
					type: 'number',
					width: 120
				}
			];
		}
	},
	methods: {
		...mapMutations(['setFreightLinesValue', 'addFreightLine', 'deleteFreightLine']),
		truncateDecimal,
		onStrInput(lineNo, key, value) {
			this.setFreightLinesValue({ lineNo, key, value });
		},
		onFloatInput(lineNo, key, strValue) {
			let floatValue = parseFloat(strValue);
			if (isNaN(floatValue))
				floatValue = 0;
			this.setFreightLinesValue({ lineNo, key, value: floatValue });
		},
		onIntInput(lineNo, key, strValue) {
			let intValue = parseInt(strValue);
			if (isNaN(intValue))
				intValue = 0;
			this.setFreightLinesValue({ lineNo, key, value: intValue });
		},
		onSelectPackageType(lineNo, event) {
			this.setPackageType(lineNo, event);
			window.focusNext();
		},
		setPackageType(lineNo, packageType) {
			if (packageType && packageType.packageCode && packageType.packageName) {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'packageTypeCode', value: packageType.packageCode });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'description', value: packageType.packageName.no });
			} else {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'packageTypeCode', value: '' });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'description', value: '' });
			}
		},
		onSelectPalletPackageType(lineNo, event) {
			this.setPalletPackageType(lineNo, event);
			window.focusNext();
		},
		setPalletPackageType(lineNo, packageType) {
			if (packageType && packageType.packageCode && packageType.packageName) {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageCode', value: packageType.packageCode });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageTypeCode', value: packageType.packageTypeCode });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorPrice', value: packageType.palletPriceFactorPrice });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorTruck', value: packageType.palletPriceFactorTruck });
			} else {
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageCode', value: '' });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPackageTypeCode', value: '' });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorPrice', value: 0 });
				this.setFreightLinesValue({ lineNo: lineNo, key: 'palletPriceFactorTruck', value: 0 });
			}
		},
		formatVolume(num) {
			return Number.parseFloat(num.toString()).toFixed(3);
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		removeLine() {
			let selectedLines = this.$refs.grid.getSelectedItems();
			for (let line of selectedLines) {
				this.deleteFreightLine( line.lineNo );
			}
			this.$refs.grid.resetSelectedRows();
		},
		addLine() {
			this.addFreightLine({
				lineNo: this.freightLines.length
					? this.freightLines[this.freightLines.length - 1].lineNo + 1
					: 1,
				description: '',
				marks: '',
				netWeight: 0,
				grossWeight: 0,
				numberOfUnits: 0,
				chargableWeight: 0,
				height: 0,
				length: 0,
				loadingMeters: 0,
				palletPackageCode: '',
				palletPackageTypeCode: '',
				palletPlacePrice: 0,
				palletPriceFactorPrice: 0,
				palletPriceFactorTruck: 0,
				numberOfPallets: 0,
				packageTypeCode: '',
				volume: 0,
				width: 0
			});
		},
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/text';

.freightlines {
	height: 100%;
}

.freightlines-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	background: white;

	.freightlines-header-details {
		@extend %text-small;
	}

	div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		background: white;
		:not(:first-child) {
			margin-left: 20px;
		}
	}
}

.freightlines-grid {
	height: calc(100% - 60px);
	width: 100%;
}
</style>